import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    Grid,
    List,
    ListItem,
    ListItemText,
    Skeleton,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getApiBaseUrl } from "../../../config/api";
import { ResultsPageContainer, ResultsPageHeader } from "./resultsUi";

function SubjectList({ subjects }) {
    if (!Array.isArray(subjects) || !subjects.length) return null;
    return (
        <List dense disablePadding>
            {subjects.map((s) => (
                <ListItem key={s.subCode} divider>
                    <ListItemText
                        primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {s.subName}
                                </Typography>
                                <Chip label={s.subCode} size="small" variant="outlined" />
                                {s.isCore ? <Chip label="Core" size="small" color="primary" /> : <Chip label="Elective" size="small" />}
                            </Box>
                        }
                        secondary={Array.isArray(s.levels) ? `Levels: ${s.levels.join(", ")}` : null}
                    />
                </ListItem>
            ))}
        </List>
    );
}

const CurriculumGuide = () => {
    const [loading, setLoading] = useState(true);
    const [curriculum, setCurriculum] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/MalawiCurriculum`);
                if (!cancelled) {
                    setCurriculum(data);
                    setError("");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || "Could not load curriculum guide");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const levels = curriculum ? ["Primary", "Secondary", "University"] : [];

    return (
        <ResultsPageContainer>
            <ResultsPageHeader
                title="Malawi Curriculum Guide"
                subtitle="Reference subjects, national exams, grading scales, and university assessment weighting used in this system."
            />

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {loading ? (
                <Skeleton variant="rounded" height={320} />
            ) : (
                levels.map((key) => {
                    const block = curriculum[key];
                    if (!block) return null;
                    return (
                        <Accordion key={key} defaultExpanded={key === "Secondary"} sx={{ mb: 1, borderRadius: 2, "&:before": { display: "none" } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={700}>{key}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, display: { xs: "none", sm: "block" } }}>
                                    {block.description}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: { xs: "block", sm: "none" } }}>
                                    {block.description}
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    {block.examAtEnd ? (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                National exam
                                            </Typography>
                                            <Typography variant="body2">{block.examAtEnd}</Typography>
                                        </Grid>
                                    ) : null}
                                    {block.examAtForm2 ? (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2">
                                                <strong>Form 2:</strong> {block.examAtForm2}
                                            </Typography>
                                        </Grid>
                                    ) : null}
                                    {block.examAtForm4 ? (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2">
                                                <strong>Form 4:</strong> {block.examAtForm4}
                                            </Typography>
                                        </Grid>
                                    ) : null}
                                    {block.assessmentWeighting ? (
                                        <Grid item xs={12}>
                                            <Chip label={block.assessmentWeighting} color="info" size="small" sx={{ mr: 1 }} />
                                            <Chip label={block.gradeThresholds} size="small" variant="outlined" />
                                        </Grid>
                                    ) : null}
                                    {block.degreeClassification ? (
                                        <Grid item xs={12}>
                                            {Object.entries(block.degreeClassification).map(([k, v]) => (
                                                <Typography key={k} variant="body2">
                                                    <strong>{k}:</strong> {v}
                                                </Typography>
                                            ))}
                                        </Grid>
                                    ) : null}
                                </Grid>
                                <SubjectList subjects={block.subjects} />
                            </AccordionDetails>
                        </Accordion>
                    );
                })
            )}
        </ResultsPageContainer>
    );
};

export default CurriculumGuide;
